import { useMutation } from '@tanstack/react-query'

import { useNames } from '@hooks/names'
import { useFindProfile } from '@hooks/profile'
import { api } from '@services/apiClient'
import { queryClient } from '@services/queryClient'

import { useFindBarriersQueries } from './queries'
import { IBarrierComment } from '../../interfaces/task/barrier'

interface IPostBarrierComment {
  comment: string
  barrierId: number
}

interface IAddBarrierComment {
  comment: string
  barrierId: number
  taskId: number
}

export function useAddBarrierComment() {
  const { data: user } = useFindProfile()
  const { getBarriersQuery } = useFindBarriersQueries()
  const { divideName } = useNames()
  const { firstName, lastName } = divideName(user?.displayName ?? '')
  const siteId = user && user.selectedSite.id

  async function postBarrierComment({
    comment,
    barrierId,
  }: IPostBarrierComment) {
    const url = `sites/${siteId}/barriers/${barrierId}/comments`
    const { data } = await api.post<IBarrierComment>(url, { comment })

    return data
  }

  return useMutation({
    mutationFn: postBarrierComment,
    onMutate: handleOptimisticAddBarrierComment,
    onError: (_err, _newBarrierComment, context) => {
      if (!context) return

      const { previousBarriers, barrierKey } = context
      queryClient.setQueryData(barrierKey, previousBarriers)
    },
    onSettled: (_barrierComments, _err, _variables, context) =>
      queryClient.invalidateQueries({
        queryKey: context?.barrierKey,
      }),
  })

  async function handleOptimisticAddBarrierComment({
    comment,
    barrierId,
    taskId,
  }: IAddBarrierComment) {
    const barriersQuery = await getBarriersQuery(taskId)
    const [barrierKey, barriersResult] = barriersQuery
    const previousBarriers = barriersResult

    const updatedBarriers = barriersResult?.map((barrier) => {
      if (barrier.id === barrierId) {
        return {
          ...barrier.comments,
          comment,
          createdOn: new Date(),
          id: -1,
          user: {
            firstName,
            lastName,
            email: user?.email ?? '',
            id: -1,
            uid: '',
          },
          barrierId: barrier.id,
          isSapComment: false,
          updatedOn: new Date(),
        }
      }
      return barrier
    })

    queryClient.setQueryData(barrierKey, () => updatedBarriers)

    return { barrierKey, updatedBarriers, previousBarriers }
  }
}
